import { ReactElement, useEffect, useState } from "react";
import { Breadcrumb, Layout, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import firebase from "firebase/app";
import moment from "moment";
import { PlusOutlined } from "@ant-design/icons";
import { ViewState, EditingState, IntegratedEditing } from "@devexpress/dx-react-scheduler";
import { Scheduler, DayView, Toolbar, DateNavigator, Appointments, TodayButton, AppointmentTooltip, CurrentTimeIndicator } from "@devexpress/dx-react-scheduler-material-ui";
import Paper from "@material-ui/core/Paper";
import { getClubState } from "../../redux/selectors";
import { BreadCrumbWrapper } from "../../components/GlobalStyles";
import { ToolbarWithLoading } from "../../components/Scheduler/ToolbarWithLoading";
import { BookingTooltipHeader } from "../../components/Scheduler/BookingTooltipHeader";
import { BookingTooltipContent } from "../../components/Scheduler/BookingTooltipContent";
import api from "../../redux/api/axiosInstance";
import { SET_LOADING_STATE, RESET_LOADING_STATE } from "../../redux/constants";

const { Content } = Layout;

const useQueryParams = (): any => {
  return new URLSearchParams(useLocation().search);
};

const BookingContent = (props: any) => {
  const { children, ...restProps } = props;
  return (
    <Appointments.Appointment {...restProps}>
      {children}
    </Appointments.Appointment>
  );
};

export function BookingHistory(): ReactElement {
  // const history = useHistory();
  const { clubs } = useSelector(getClubState);
  const [currentClub, setCurrentClub] = useState<any>(clubs[0] || {});
  const [bookings, setBookings] = useState<any[]>([]);
  const [currentDate, setCurrentDate] = useState<any>(new Date());
  const { clubId } = useParams<{ clubId: string }>();
  const [clubName, setClubName] = useState<string>(currentClub.name);
  const [branchName, setBranchName] = useState<string>("");
  const [courtName, setCourtName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation();
  const query = useQueryParams();
  const branchId = query.get("branch");
  const courtId = query.get("court");
  const windowWidth = window.innerWidth;
  const dispatch = useDispatch();

  useEffect(() => {
    async function getBookings() {
      const branchIds = [];
      if (branchId === null) {
        // getBranchIds with ClubId
        const branchSnapshots = await firebase.firestore().collection("branches").where("club", "==", clubId).get();
        branchSnapshots.docs.forEach((doc, index) => {
          const data = doc.data();
          branchIds.push(data.firestoreRef);
        });
      } else {
        branchIds.push(branchId);
      }
      if (branchIds.length === 0) {
        setBookings([]);
        return;
      }
      const bookingData: any[] = [];
      const bookingSnapshots = await firebase.firestore().collection("bookings").where("branchId", "in", branchIds).where("status", "!=", "cancelled").get();
      await Promise.all(
        bookingSnapshots.docs.map(async (doc: any, index: number) => {
          const data = doc.data();
          const branchSnapshot = await firebase.firestore().collection("branches").doc(data.branchId).get();
          const branch = branchSnapshot.data();
          bookingData.push({
            title: ``,
            startDate: moment(`${data.date} ${data.start}`).toDate(),
            endDate: moment(`${data.date} ${data.start}`)
              .add(data.period, "m")
              .toDate(),
            id: data.bookingId,
            ...data,
            branch: branch?.location,
            clubId: branch?.club,
            user: data.user || null,
          });
        })
      );
      await Promise.all(
        bookingData.map(async (data, index) => {
          // const clubSnapshot = await firebase.firestore().collection("clubs").doc(data.clubId).get();
          // const clubData = clubSnapshot.data();
          // bookingData[index].club = clubData;
          // bookingData[index].title = `${clubData?.name}, ${bookingData[index].branch}`;
          bookingData[index].club = currentClub;
          bookingData[index].title = `${currentClub.name}, ${bookingData[index].branch}`;
        })
      );
      await Promise.all(
        bookingData.map(async (data, index) => {
          const courtSnapshot = await firebase.firestore().collection("courts").doc(data.courtId).get();
          const courtData = courtSnapshot.data();
          bookingData[index].court = courtData;
          bookingData[index].title = `${bookingData[index].title}, ${courtData?.name}`;
        })
      );
      await Promise.all(
        bookingData.map(async (data, index) => {
          if (data.userId !== "") {
            const userSnapshot = await firebase.firestore().collection("users").doc(data.userId).get();
            const userData = userSnapshot.data();
            bookingData[index].user = userData;
          }
          bookingData[index].title = `${bookingData[index].user.fullName}`;
        })
      );
      if (courtId === null) {
        setBookings(bookingData);
      } else {
        const filteredBookindData = bookingData.filter((b) => b.courtId === courtId);
        setBookings(filteredBookindData);
      }
      setLoading(false);
    }
    getBookings();
  }, []);

  useEffect(() => {
    // Get clubs 
    api.post("getClub", { clubId })
      .then(res => {
        const { clubData } = res.data;
        const branchesOfClub = res.data.branches;
        const courtsOfClub = res.data.courts;
        setClubName(clubData.name);
        setCurrentClub(clubData);

        if (branchId) {
          const branchData = branchesOfClub.find((b: any) => b.firestoreRef);
          setBranchName(branchData?.location);
        }
        if (courtId) {
          const currentCourt = courtsOfClub.find((c: any) => c.courtId === courtId);
          setCourtName(currentCourt?.name);
        }
      })
  }, []);

  const cancelBooking = (bookingId: string) => {
    dispatch({ type: SET_LOADING_STATE });
    console.log(bookingId);
    api.post("/cancel_booking", { bookingId })
      .then(() => {
        const bookingState = bookings.filter((e) => e.bookingId !== bookingId);
        setBookings(bookingState);
        setLoading(false);
        message.success("Booking has been removed!");
        dispatch({ type: RESET_LOADING_STATE });
      })
      .catch(e => {
        setLoading(false);
        message.error("Oop! Something went wrong!");
        dispatch({ type: RESET_LOADING_STATE });
      })
  };

  const onCommitChanges = (data: any) => {
    const { added, changed, deleted } = data;
    if (deleted !== undefined) {
      const confirm = window.confirm("Would you remove this booking?");
      if (confirm) {
        cancelBooking(deleted);
      }
    }
  };
  return (
    <Content>
      <BreadCrumbWrapper>
        <Breadcrumb style={{ fontSize: 18 }}>
          <Breadcrumb.Item>
            <a href="/clubs">{t("club.clubs")}</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <a href={`/clubs/${clubId}/bookings`}>{clubName}</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            {branchId === null ? (
              branchName
            ) : (
              <a href={`/clubs/${clubId}/bookings?branch=${branchId}`}>
                {branchName}
              </a>
            )}
          </Breadcrumb.Item>
          <Breadcrumb.Item>{courtName}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <Paper>
        <Scheduler data={bookings}>
          <ViewState
            currentDate={currentDate}
            onCurrentDateChange={(date) => setCurrentDate(date)}
          />
          <EditingState onCommitChanges={onCommitChanges} />
          <IntegratedEditing />
          <DayView
            startDayHour={1}
            endDayHour={24}
            cellDuration={60}
            intervalCount={windowWidth > 768 ? 5 : 2}
          />
          <Toolbar
            {...(loading ? { rootComponent: ToolbarWithLoading } : null)}
          />
          <DateNavigator />
          <TodayButton />
          <Appointments appointmentComponent={BookingContent} />
          <AppointmentTooltip
            headerComponent={BookingTooltipHeader}
            contentComponent={BookingTooltipContent}
            showDeleteButton
            showCloseButton
          />
          <CurrentTimeIndicator shadePreviousCells shadePreviousAppointments />
        </Scheduler>
      </Paper>
      {/* <Button
        type="primary"
        style={{ position: "absolute", right: 25, top: 95 }}
        icon={<PlusOutlined />}
        onClick={() => setVisibleImportDrawer(true)}
      >
        Import Book
      </Button> */}
    </Content>
  );
}
